<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
	<div class="flex items-center justify-center h-screen">
		<div class="box">
			<img src="/images/login-logo.png" class="logo block mx-auto" alt="Logo">
			<Transition name="errormessage">
				<div v-show="errorMessage">
					<Message class="ml-8 mr-8 mb-8" severity="error">
						<template #icon>
							<GlyphIcon :icon="GlyphIconName['triangle-empty-alert']" size="32" color="var(--p-message-error-color)" />
						</template>
						{{  errorMessage }}
					</Message>
				</div>
			</Transition>
			<form @submit.prevent="login">
				<Transition name="section">
					<div class="pl-8 pr-8" v-show="mode===LoginDialogMode.USERNAME_PASSWORD">

						<FloatLabel variant="on" class="mb-4">
							<InputText v-model="auth.username" type="email" id="email" @keyup.enter="login" :disabled="isWorking" fluid />
							<label for="email">{{  t('login.email') }}</label>
						</FloatLabel>

						<FloatLabel variant="on">
							<Password v-model="auth.password" id="password" :feedback="false" @keyup.enter="login" :disabled="isWorking" fluid />
							<label for="password">{{  t('login.password') }}</label>
						</FloatLabel>

						<div class="text-right">
							<Button type="button" variant="link" @click="showReset()" size="small">{{ t('login.resetPassword') }}</Button>
						</div>

						<div class="flex justify-center items-center space-x-2 mt-2">
							<ToggleSwitch v-model="auth.persist" :disabled="isWorking" />
							<label @click="auth.persist=!auth.persist">{{ t('login.remember') }}</label>
						</div>
					</div>
				</Transition>

				<Transition name="section">
					<div class="flex flex-col items-center" v-show="mode===LoginDialogMode.SMS_ENTRY">
						<label for="smsKey">{{ t('login.smsCode') }}</label>
						<InputOtp v-model="auth.smsKey" :length="4" size="large" id="smsKey" @keyup.enter="login" :disabled="isWorking"
						:pt="{ input: { 'autocomplete': 'section-login one-time-code' } }" />
					</div>
				</Transition>

				<Transition name="section">
					<div class="flex flex-col items-center" v-show="mode===LoginDialogMode.AUTHENTICATOR_ENTRY || mode===LoginDialogMode.AUTHENTICATOR_SETUP">
						<label v-show="mode===LoginDialogMode.AUTHENTICATOR_SETUP" for="authenticatorQrUrl">{{ t('login.authenticatorSetup') }}</label>
						<img v-show="mode===LoginDialogMode.AUTHENTICATOR_SETUP" :src="authenticatorQrUrl" id="authenticatorQrUrl" alt="Authenticator QR code" class="mb-3" />
						<label for="authenticatorToken">{{ t('login.authenticatorCode') }}</label>
						<InputOtp v-model="auth.authenticatorToken" id="authenticatorToken" :length="6" size="large" @keyup.enter="login" :disabled="isWorking"
							:pt="{ input: { 'autocomplete': 'section-login one-time-code' } }" />
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 flex flex-col justify-center" v-show="mode===LoginDialogMode.PASSWORD_RESET">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="flex justify-center items-center space-x-2">
							<div class="w-72 mb-6 mt-3 text-pretty">{{ t('login.resetPasswordHelp') }}</div>
						</div>
						<FloatLabel variant="on">
							<InputText v-model="resetData.username" type="email" fluid id="resetemail" @keyup.enter="reset" :disabled="isWorking" :minlength="3" />
							<label for="resetemail">{{ t('login.email') }}</label>
						</FloatLabel>
						<Select v-model="resetData.method" class="text-left mt-3" fluid :options="resetMethods" option-label="text" option-value="value" append-to="self" />
						<div class="mt-5 flex justify-center space-x-2">
							<Button @click="gotoUsernamePassword()" severity="secondary" fluid :disabled="isWorking">{{ t('common.cancel') }}</Button>
							<Button @click="reset()" severity="primary" fluid :disabled="isWorking || !resetData.username">{{ t('login.next') }}</Button>
						</div>
					</div>
				</Transition>

				<Transition name="section">
					<div v-show="mode===LoginDialogMode.PASSWORD_RESET_EMAIL_COMPLETED">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="mb-4 mt-2">{{ t('login.resetPasswordHelp2') }}</div>
						<Button @click="gotoUsernamePassword(true)" fluid :disabled="isWorking">{{ t('login.ok') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div v-show="mode===LoginDialogMode.PASSWORD_RESET_SMS_COMPLETED">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="mb-4 mt-2">{{ t('login.resetPasswordHelp3') }}</div>
						<Button @click="gotoUsernamePassword(true)" fluid :disabled="isWorking">{{ t('login.ok') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 pt-4 mt-5 flex justify-center space-x-2" v-show="!mode.startsWith('PASSWORD_RESET')">
						<Button @click="gotoUsernamePassword()" severity="secondary" fluid v-if="mode!==LoginDialogMode.USERNAME_PASSWORD" >{{ t('common.cancel') }}</Button>
						<Button @click="login()" fluid :disabled="loginButtonDisabled">{{ t('login.loginButton') }}</Button>
					</div>
				</Transition>
			</form>

		</div>
	</div>
</template>

<script setup lang="ts">
import axios, { AxiosError } from 'axios'
import { useSettingsStore } from '@/stores/Settings'
import { useI18n } from 'vue-i18n'
import { type LoginResponse, type LoginRequest, type ValidateTokenExtendedResponse, type ResetRequest, type ResetResponse, LoginResponseErrorCodes, ResetResponseErrorCodes } from '@/TcxApi/Authentication'
import { ref, computed, onMounted } from 'vue'
import { LoginDialogMode } from '@/definitions/Common'
import { CustomerFeature } from '@/TcxApi/Customer'
import type { ErrorResponse } from '@/TcxApi/ErrorResponse'
import Message from 'primevue/message'
import GlyphIcon from './GlyphIcon.vue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const { t } = useI18n()
const ss = useSettingsStore()
const auth = ref<LoginRequest>({ username: '', password: '', smsKey: '', authenticatorToken: '', cookie: false, persist: false })
const errorMessage = ref<string | null>(null)
const isWorking = ref(false)
const mode = ref<LoginDialogMode>(LoginDialogMode.USERNAME_PASSWORD)
const authenticatorQrUrl = ref<string | undefined>(undefined)
const resetData = ref<ResetRequest>({ username: '', method: 'EMAIL' })
const resetMethods = ref<{ value: string, text: string }[]>([
	{ value: 'EMAIL', text: t('login.sendToEmail') },
	{ value: 'SMS', text: t('login.sendToSms') }
])

async function login() {
	if (!auth.value.username || !auth.value.password) { return }
	isWorking.value = true
	errorMessage.value = null

	try {
		const res = await axios.post<LoginResponse>(`${ss.apiUrl}/auth/login`, auth.value, { timeout: 10000 })

		if (!res.data.customerFeatures.includes(CustomerFeature.PBX) || !res.data.communicatorAccess) {
			errorMessage.value = t('login.errorNoCommunicatorAccess')
			auth.value.smsKey = ''
			auth.value.authenticatorToken = ''
			mode.value = LoginDialogMode.USERNAME_PASSWORD
			setTimeout(() => { document.getElementById('password')?.focus() }, 10)
			return
		}

		isWorking.value = false
		authenticatorQrUrl.value = undefined
		mode.value = LoginDialogMode.USERNAME_PASSWORD

		ss.runtime.rememberMe = auth.value.persist
		ss.runtime.emailAddress = auth.value.username
		ss.initAfterLoginOrValidate(res.data as ValidateTokenExtendedResponse)
	}
	catch (res) {
		isWorking.value = false

		if (mode.value === LoginDialogMode.SMS_ENTRY || mode.value === LoginDialogMode.AUTHENTICATOR_ENTRY) {
			auth.value.smsKey = ''
			auth.value.authenticatorToken = ''
			mode.value = LoginDialogMode.USERNAME_PASSWORD
			setTimeout(() => { document.getElementById('password')?.focus() }, 500)
		}

		if (!(res as AxiosError).response?.data) {
			errorMessage.value = t('login.errorNoConnection')
			return
		}

		const err = ((res as AxiosError).response?.data as ErrorResponse)?.error

		if (err.status === 500) {
			errorMessage.value = t('login.errorServer')
		} else {
			switch (err.code) {
				case LoginResponseErrorCodes.credentials_invalid:
					errorMessage.value = t('login.errorCredentials')
					document.getElementById('password')?.focus()
					break
				case LoginResponseErrorCodes.account_disabled:
					errorMessage.value = t('login.errorDisabled')
					break
				case LoginResponseErrorCodes.ipaddress_invalid:
					errorMessage.value = t('login.errorWrongIp')
					break
				case LoginResponseErrorCodes.sms_error:
					errorMessage.value = t('login.errorServer')
					break
				case LoginResponseErrorCodes.sms_authenticate:
					mode.value = LoginDialogMode.SMS_ENTRY
					setTimeout(() => {
						(document.querySelector('#smsKey>INPUT') as HTMLInputElement)?.focus()
					}, 500)
					break
				case LoginResponseErrorCodes.sms_key_invalid:
					errorMessage.value = t('login.errorSmsCodeInvalid')
					break
				case LoginResponseErrorCodes.authenticator_authenticate:
					mode.value = LoginDialogMode.AUTHENTICATOR_ENTRY
					setTimeout(() => {
						(document.querySelector('#authenticatorToken>INPUT') as HTMLInputElement)?.focus()
					}, 500)
					break
				case LoginResponseErrorCodes.authenticator_key_invalid:
					errorMessage.value = t('login.authenticatorCodeInvalid')
					break
				case LoginResponseErrorCodes.authenticator_setup:
					mode.value = LoginDialogMode.AUTHENTICATOR_SETUP
					setTimeout(() => { document.getElementById('authenticatorToken')?.focus() }, 500)
					authenticatorQrUrl.value = `https://api.telecomx.dk/tools/qr?text=${encodeURIComponent((err as unknown as { totp: string }).totp)}`
					break
			}
		}
	}
}

function showReset() {
	mode.value = LoginDialogMode.PASSWORD_RESET
	errorMessage.value = null
	setTimeout(() => { document.getElementById('resetemail')?.focus() }, 500)
}

async function reset() {
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetData.value.username)) { return } // Ensure valid email address
	isWorking.value = true
	errorMessage.value = null

	try {
		const res = await axios.post<ResetResponse>(`${ss.apiUrl}/auth/reset`, resetData.value, { timeout: 10000 })

		isWorking.value = false
		if (res.data.method === 'EMAIL') {
			mode.value = LoginDialogMode.PASSWORD_RESET_EMAIL_COMPLETED
		} else {
			mode.value = LoginDialogMode.PASSWORD_RESET_SMS_COMPLETED
		}
	}
	catch (res) {
		isWorking.value = false

		if (!(res as AxiosError).response?.data) {
			errorMessage.value = t('login.errorNoConnection')
			return
		}

		const err = ((res as AxiosError).response?.data as ErrorResponse).error

		if (err.status === 500) {
			errorMessage.value = t('login.errorServer')
		} else {
			switch (err.code) {
				case ResetResponseErrorCodes.account:
					errorMessage.value = t('login.errorDisabled')
					break
				case ResetResponseErrorCodes.username:
					errorMessage.value = t('login.errorResetNotAvailable')
					break
				case ResetResponseErrorCodes.time:
					errorMessage.value = t('login.errorResetAlreadyInProgress')
					break
			}
		}
	}
}

function gotoUsernamePassword(fill = false) {
	mode.value = LoginDialogMode.USERNAME_PASSWORD
	if (fill) {
		auth.value.username = resetData.value.username
	} else {
		resetData.value.username = ''
	}
	auth.value.password = ''
	auth.value.smsKey = ''
	auth.value.authenticatorToken = ''

	resetData.value.method = 'EMAIL'
	errorMessage.value = null

	setTimeout(() => {
		if (auth.value.username) {
			document.getElementById('password')?.focus()
		} else {
			document.getElementById('email')?.focus()
		}
	}, 500)
}

const loginButtonDisabled = computed(() => {
	if (isWorking.value) { return true }
	if (!auth.value.username || !auth.value.password) { return true }
	if (mode.value === LoginDialogMode.SMS_ENTRY && (!auth.value.smsKey || (auth.value.smsKey && auth.value.smsKey.length < 4))) { return true }
	if (mode.value === LoginDialogMode.AUTHENTICATOR_ENTRY && (!auth.value.authenticatorToken || (auth.value.authenticatorToken && auth.value.authenticatorToken.length < 6))) { return true }
	return false
})

onMounted(() => {
	auth.value.username = ss.runtime.emailAddress ?? ''
	setTimeout(() => {
		if (auth.value.username) {
			document.getElementById('password')?.focus()
		} else {
			document.getElementById('email')?.focus()
		}
	}, 10)
})
</script>

<style scoped>
	.box {
		width: 500px;
		padding: 30px;
		background-color: var(--p-surface-300);
		text-align: center;
		border-radius: 10px;
		box-shadow: var(--modal-shadow);
	}
	@media(prefers-color-scheme: dark) {
		.box {
			background-color: var(--p-surface-900);
		}
	}
	.logo {
		width: 300px;
		padding-bottom: 25px;
	}

	.errormessage-enter-active {
		transition:
			max-height 0.4s ease-out,
			opacity 0.4s ease-out 0.4s;
		overflow: hidden;
	}

	.errormessage-leave-active {
		transition: max-height 0.1s ease-out, opacity 0.1s ease-out;
		overflow: hidden;
	}

	.errormessage-enter-from,
	.errormessage-leave-to {
		max-height: 0;
		opacity: 0;
	}

	.errormessage-enter-to,
	.errormessage-leave-from {
		max-height: 200px;
		/* Set a large enough value */
		opacity: 1;
	}

	.section-enter-active {
		transition: max-height 0.2s ease-out 0.2s;
		overflow: hidden;
	}

	.section-leave-active {
		transition: max-height 0.2s ease-out;
		overflow: hidden;
	}

	.section-enter-from,
	.section-leave-to {
		max-height: 0;
	}

	.section-enter-to,
	.section-leave-from {
		max-height: 200px;
	}

</style>
